import { Box, Typography, Paper } from "@mui/material";
import VirtualizedTable from '../../components/common/VirtualizedTable';
import CellModal from '../../components/common/CellModal';
import { useState } from 'react';

const OperatorAnalyticsTable = ({ data, loading, name='Аналитика по операторам', skillType = 'soft' }) => {
  // Агрегация данных по операторам
  const aggregatedData = data.reduce((acc, item) => {
    const agentName = item.assessment.agent_name;
    
    if (!acc[agentName]) {
      acc[agentName] = {
        id: Object.keys(acc).length + 1,
        agent_name: agentName,
        total_score_sum: 0,
        count: 0,
        criteria_stats: {},
        duration_sum: 0
      };
    }
    
    const current = acc[agentName];
    current.count += 1;
    current.duration_sum += item.sound.duration;
    
    // Используем pre-calculated score из оценки
    const scoreKey = skillType === 'hard' ? 'total_score_hard' : 'total_score_soft';
    const score = item.assessment[scoreKey] || 0;
    current.total_score_sum += score;
    
    // Сбор статистики по критериям
    item.assessment.items.forEach(item => {
      // Фильтруем по type_skills
      if (item.type_skills !== skillType) return;

      let score = item.score;
      if (!item.positive) {
        score = 1 - score;
      }
      
      if (!current.criteria_stats[item.criteria_name]) {
        current.criteria_stats[item.criteria_name] = { total_score: 0, count: 0 };
      }
      current.criteria_stats[item.criteria_name].total_score += score;
      current.criteria_stats[item.criteria_name].count += 1;
    });
    
    return acc;
  }, {});

  // Преобразование в массив и расчет средних значений
  const rows = Object.values(aggregatedData).map(operator => {
    const avgTotalScore = operator.total_score_sum / operator.count;

    // Расчет среднего по каждому критерию
    const criteriaAverages = Object.keys(operator.criteria_stats).map(name => ({
      name,
      avg: operator.criteria_stats[name].total_score / operator.criteria_stats[name].count
    }));
    
    // Топ-3 лучших и худших критериев
    const top3Best = [...criteriaAverages]
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 3)
      .map(item => `${item.name} (${item.avg.toFixed(2)})`)
      .join(', ');
      
    const top3Worst = [...criteriaAverages]
      .sort((a, b) => a.avg - b.avg)
      .slice(0, 3)
      .map(item => `${item.name} (${item.avg.toFixed(2)})`)
      .join(', ');

    // Используем pre-calculated score из оценки
    const scoreKey = skillType === 'hard' ? 'total_score_hard' : 'total_score_soft';
    const totalScores = data
      .filter(item => item.assessment.agent_name === operator.agent_name)
      .map(item => item.assessment[scoreKey] || 0);
    
    const minTotalScore = Math.min(...totalScores);
    const maxTotalScore = Math.max(...totalScores);
      
    return {
      id: operator.id,
      agent_name: operator.agent_name,
      avg_total_score: Math.round(avgTotalScore * 100), // Перевод в проценты и округление до целых
      min_total_score: Math.round(minTotalScore * 100), // Минимальный балл в процентах и округление до целых
      max_total_score: Math.round(maxTotalScore * 100), // Максимальный балл в процентах и округление до целых
      call_count: operator.count,
      avg_duration: (operator.duration_sum / operator.count),
      top3_best_criteria: top3Best,
      top3_worst_criteria: top3Worst
    };
  });
  
  // Определение колонок для TanStack Table
  const columns = [
    {
      accessorKey: 'agent_name',
      header: 'Оператор',
      size: 200,
      enableSorting: true,
    },
    {
      accessorKey: 'avg_total_score',
      header: 'Средний балл (%)',
      size: 150,
      enableSorting: true,
    },
    {
      accessorKey: 'min_total_score',
      header: 'Мин. балл (%)',
      size: 150,
      enableSorting: true,
    },
    {
      accessorKey: 'max_total_score',
      header: 'Макс. балл (%)',
      size: 150,
      enableSorting: true,
    },
    {
      accessorKey: 'call_count',
      header: 'Кол-во звонков',
      size: 300,
      enableSorting: true,
    },
    {
      accessorKey: 'avg_duration',
      header: 'Ср. длительность (сек)',
      cell: info => info.getValue().toFixed(1),
      size: 300,
      enableSorting: true,
    },

  ];

  // Функция для подсветки ячеек
  const getCellStyle = (value, columnId, row) => {
    if (columnId === 'avg_total_score') {
      if (value >= 97) return { background: '#d4edda', color: '#155724' }; // Хорошо: зеленый
      if (value >= 95) return { background: '#fff3cd', color: '#856404' }; // Удовл.: желтый
      return { background: '#f8d7da', color: '#721c24' }; // Плохо: красный
    }
    // if (columnId === 'min_total_score') {
    //   if (value >= 80) return { background: '#d4edda', color: '#155724' };
    //   return { background: '#f8d7da', color: '#721c24' };
    // }
    // if (columnId === 'max_total_score') {
    //   if (value < 90) return { background: '#fff3cd', color: '#856404' };
    // }
    return {};
  };

  // Состояние для модального окна
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCellData, setSelectedCellData] = useState(null);

  // Обработчик клика по ячейке
  const handleCellClick = (value, columnId, row, extraData, event) => {
    // Устанавливаем данные для модального окна
    setSelectedCellData({ value, columnId, row, extraData });
    // Открываем модальное окно
    setModalOpen(true);
  };

  return (
    <>
      {data.length > 0 && (
        <Box sx={{ height: '100%', width: '100%', mt: 4 }}>
          <Paper sx={{ p: 2, height: '100%', width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {name}
            </Typography>
            <VirtualizedTable 
              data={rows} 
              columns={columns}
              getCellStyle={getCellStyle}
              noDataText="Нет данных для отображения аналитики"
              tableName={name}
              frozenColumnCount={0}
              onCellClick={handleCellClick}
            />
          </Paper>
        </Box>
      )}
      
      {!data.length && !loading && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          Нет данных для отображения аналитики
        </Typography>
      )}
      
      {/* Модальное окно для данных ячейки */}
      <CellModal 
        open={modalOpen} 
        onClose={() => setModalOpen(false)} 
        cellData={selectedCellData} 
      />
    </>
  );
};

export default OperatorAnalyticsTable;